import { MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  MODAL_TITLES,
} from "@miplatform/constants";
import { mdiTrashCanOutline } from "@mdi/js";
import { Box, useTheme } from "@mui/material";
import { IPriceLevelPayload } from "../PricingMatrix.types";

export function DeleteLevelModal({
  open,
  selectedPriceLevel,
  handleDeletePriceLevel,
  setShowDeleteModal,
  setSelectedPriceLevel,
}: DeleteLevelModalProps) {
  const theme = useTheme();
  const { name, assignedAccounts = [] } = selectedPriceLevel || {};

  const hasAssignedAccounts = assignedAccounts.length > 0;

  const description = hasAssignedAccounts ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Box>
        This price level has associated customers. To delete it, you must
        reassign those customers to a different price level.
      </Box>
      <Box>
        Tip: Hover over the info icon in the price level header to view
        associated customers.
      </Box>
    </Box>
  ) : (
    <>
      Are you sure you want to delete{" "}
      <span
        style={{
          fontWeight: theme.fontWeight.semiBold,
          color: theme.palette.primary.main,
        }}
      >
        {name}{" "}
      </span>
      price level? Deleting a price level cannot be undone.
    </>
  );

  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setSelectedPriceLevel(null);
  };

  const actions = hasAssignedAccounts
    ? [
        {
          name: "Got it",
          variant: BUTTON_VARIANTS.CONTAINED,
          onClick: handleCloseModal,
        },
      ]
    : [
        {
          name: BUTTON_LABELS.CANCEL,
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: handleCloseModal,
        },
        {
          name: BUTTON_LABELS.DELETE,
          variant: BUTTON_VARIANTS.CONTAINED,
          color: BUTTON_COLORS.ERROR,
          onClick: () => {
            if (selectedPriceLevel) {
              handleDeletePriceLevel(selectedPriceLevel);
            }
            handleCloseModal();
          },
        },
      ];

  return (
    <MiModal
      open={open}
      closeModal={handleCloseModal}
      title={
        hasAssignedAccounts
          ? "Unable to Delete Price Level"
          : MODAL_TITLES.DELETE
      }
      titleIcon={mdiTrashCanOutline}
      leftIconColor={theme.palette.error.main}
      description={description}
      actions={actions}
    />
  );
}

interface DeleteLevelModalProps {
  open: boolean;
  selectedPriceLevel: IPriceLevelPayload | null;
  handleDeletePriceLevel: (level: IPriceLevelPayload) => void;
  setShowDeleteModal: (show: boolean) => void;
  setSelectedPriceLevel: (level: IPriceLevelPayload | null) => void;
}
